import { CognitoUser } from "@aws-amplify/auth";
import { Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { usePostHog } from "@posthog/react";
import { convertToHubUser } from "contexts/identity-context";
import * as localStorageKeys from "data/local-storage-keys";
import { confirmSignUp, signIn } from "data/mutations-common";
import { Form, Formik } from "formik";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router";
import * as Routes from "routes";
import { InferType, boolean, object, string } from "yup";
import { useIdentity } from "../../contexts/identity-context";
import { MfaLogin } from "./mfa-login";

const minPasswordLength = 8;

export const SignInValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
  password: string()
    .min(
      minPasswordLength,
      `Password should be of minimum ${minPasswordLength} characters length`,
    )
    .required("Password is required"),
  termsOfUse: boolean(),
});

type SignInValues = InferType<typeof SignInValidationSchema>;

export const SignIn = () => {
  const [_, setUserState] = useIdentity();
  const { search } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isTermsOfUseRequired, setIsTermsOfUseRequired] = useState(false);
  const optOutMfa = localStorage.getItem(localStorageKeys.optOutMfa);
  const mfaSetupRequired = user && !user.challengeName && optOutMfa !== "true";
  const posthog = usePostHog();

  const confirmSignUpMutation = useMutation({
    mutationFn: async ({
      username,
      code,
    }: {
      username: string;
      code: string;
    }) => {
      return await confirmSignUp(username, code);
    },
  });

  useEffect(() => {
    (async function () {
      const params = queryString.parse(search);
      if (params.code && params.username) {
        const username = String(params["username"]);
        const code = String(params["code"]);
        confirmSignUpMutation.mutate({ username, code });
      }
    })();
  }, [search]);

  const convertToHubUserMutation = useMutation({
    mutationFn: (cognitoUser: CognitoUser) => convertToHubUser(cognitoUser),
    onSuccess: (user) => {
      setUserState(user);
      setMfaRequired(false);
    },
  });

  const signInMutation = useMutation({
    mutationFn: async (values: SignInValues) => {
      return await signIn(values);
    },
    onSuccess: (cognitoUser) => {
      setUser(cognitoUser);

      if (
        cognitoUser.challengeName === "SMS_MFA" ||
        cognitoUser.challengeName === "SOFTWARE_TOKEN_MFA"
      ) {
        setMfaRequired(true);
        return;
      }
      posthog.capture("user_signed_in");
      convertToHubUserMutation.mutate(cognitoUser);
    },
    onError: (error: any) => {
      if (error.message.includes("terms_of_use_failure")) {
        setIsTermsOfUseRequired(true);
      }
      posthog.capture("user_failed_signing_in");
    },
  });

  const getErrorMessage = (error: any): string => {
    if (error.message.includes("terms_of_use_failure")) {
      return "Please accept Geoscape Hub's Terms of Use to continue.";
    } else if (error.code === "UserNotFoundException") {
      return "Invalid username or password. Please try again.";
    } else if (error.code === "NotAuthorizedException") {
      if (error.message.includes("disabled")) {
        return "Account disabled. Please contact support";
      } else {
        return "Invalid username or password. Please try again.";
      }
    } else if (error.code === "UserNotConfirmedException") {
      return "This account needs to be verified before signing in. Check your email or use the 'Resend Verification' form to send a new confirmation.";
    } else {
      console.error(`User sign-in failure: ${error.message}`);
      return "We encountered an unexpected error. Please try again in a few minutes or contact support";
    }
  };

  return (
    <>
      {!mfaRequired && !mfaSetupRequired && (
        <Formik<SignInValues>
          initialValues={{ email: "", password: "", termsOfUse: false }}
          onSubmit={(values) => signInMutation.mutate(values)}
          validationSchema={SignInValidationSchema}
        >
          {({
            values: { email, password, termsOfUse },
            handleChange,
            handleBlur,
            errors,
            touched,
            setFieldValue,
          }) => (
            <Form id="signinForm">
              <div className="flex flex-col items-center min-w-[100vw]">
                <Card className="min-w-[340px] max-w-[500px] m-2">
                  <CardHeader>
                    <CardTitle className="text-xl">
                      Sign into your account
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="text"
                        value={email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={signInMutation.isPending}
                        className={
                          touched.email && errors.email
                            ? "border-destructive"
                            : ""
                        }
                      />
                      {touched.email && errors.email && (
                        <p className="text-xs text-destructive">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={signInMutation.isPending}
                          className={`pr-10 ${touched.password && errors.password ? "border-destructive" : ""}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                        </Button>
                      </div>
                      {touched.password && errors.password && (
                        <p className="text-xs text-destructive">
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {isTermsOfUseRequired && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="termsOfUse"
                          checked={termsOfUse}
                          onCheckedChange={(checked) =>
                            setFieldValue("termsOfUse", checked)
                          }
                        />
                        <Label htmlFor="termsOfUse" className="text-sm">
                          {"I agree to Geoscape Hub's "}
                          <a
                            href="https://geoscape.com.au/legal/geoscape-developer-terms"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline underline-offset-4"
                          >
                            Terms of Use
                          </a>
                        </Label>
                      </div>
                    )}

                    {signInMutation.isPending ||
                    confirmSignUpMutation.isPending ? (
                      <div className="flex justify-center w-full py-2">
                        <Spinner />
                      </div>
                    ) : (
                      <>
                        {confirmSignUpMutation.isSuccess && (
                          <Alert>
                            <AlertDescription>
                              Your account is successfully confirmed.
                            </AlertDescription>
                          </Alert>
                        )}

                        {confirmSignUpMutation.isError && (
                          <Alert variant="destructive">
                            <AlertDescription>
                              An error occurred during account confirmation.
                            </AlertDescription>
                          </Alert>
                        )}

                        {signInMutation.isError && (
                          <Alert variant="destructive">
                            <AlertDescription>
                              {getErrorMessage(signInMutation.error)}
                            </AlertDescription>
                          </Alert>
                        )}

                        {convertToHubUserMutation.isError && (
                          <Alert variant="destructive">
                            <AlertDescription>
                              {(convertToHubUserMutation.error as Error)
                                ?.message || "Failed to fetch user information"}
                            </AlertDescription>
                          </Alert>
                        )}

                        <Button
                          id="sign-in"
                          type="submit"
                          disabled={signInMutation.isPending}
                          className="w-full"
                        >
                          Sign In
                        </Button>
                      </>
                    )}

                    <div className="flex justify-center gap-1">
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.signUp}>Sign up</a>
                      </Button>
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.resetPassword}>Password Reset</a>
                      </Button>
                      <Button variant="link" size="sm" asChild>
                        <a href={Routes.resendVerification}>
                          Resend Verification
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </Form>
          )}
        </Formik>
      )}

      {mfaRequired && (
        <MfaLogin
          onSubmit={async (cognitoUser) => {
            convertToHubUserMutation.mutate(cognitoUser);
          }}
          user={user}
        />
      )}
    </>
  );
};
